// Small presentational pieces. No data fetching, no HTTP, no domain rules — see api/client.ts.
import type { ReactNode } from 'react';
import { identityColour, initials } from '../lib/colour.ts';
import { hours } from '../lib/dates.ts';
import { loadLevel, loadText, type DayCapacity } from '../lib/capacity.ts';

export function Avatar({ id, name, isTeam }: { id: string; name: string | null; isTeam?: boolean }) {
  const c = identityColour(id, isTeam);
  return (
    <span className="avatar" style={{ background: c.bg, borderColor: c.border, color: c.text }}
          title={name ?? ''} aria-hidden>
      {initials(name)}
    </span>
  );
}

/**
 * The load bar. COLOUR IS THE FAST PATH AND THE NUMBER IS THE TRUTH — the numeric title and the
 * adjacent text carry the same information for anybody who cannot separate the hues.
 */
export function LoadBar({ assigned, capacity }: { assigned: number; capacity: number }) {
  const level = loadLevel(assigned, capacity);
  const pct = capacity > 0 ? Math.min(100, (assigned / capacity) * 100) : (assigned > 0 ? 100 : 0);
  return (
    <div className={`load ${level}`} title={`${hours(assigned)} van ${hours(capacity)}`}
         role="img" aria-label={`${hours(assigned)} ingepland van ${hours(capacity)} beschikbaar`}>
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * "6 / 8 u", coloured. The single thing a planner looks at before promising a customer a date.
 *
 * Both numbers, always — not a percentage, not a bar alone. "75%" of an unknown day is unusable, and
 * a bar without a figure cannot answer "can he still take four hours". The colour repeats what the
 * numbers already say, so it is a shortcut rather than the only channel.
 */
export function CapacityPill({ c, compact }: { c: DayCapacity; compact?: boolean }) {
  if (c.nonWorking) return <span className="pill nonwork" title="Geen werkdag">—</span>;
  const level = loadLevel(c.assignedMinutes, c.capacityMinutes);
  const label = level === 'over' ? 'te veel ingepland'
    : level === 'full' ? 'volledig ingepland'
    : level === 'empty' ? 'nog niets ingepland' : 'nog ruimte';
  return (
    <span className={`pill ${level}${compact ? ' compact' : ''}`} title={label}
          aria-label={`${loadText(c)} — ${label}`}>
      {loadText(c)}
    </span>
  );
}

/** A control the backend cannot serve yet. VISIBLE AND DISABLED, never silently missing. */
export function NotYet({ what, children }: { what: string; children: ReactNode }) {
  return (
    <span title={`Nog niet beschikbaar: ${what}`} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', opacity: .55 }}>
      {children}<span className="badge ro">binnenkort</span>
    </span>
  );
}

export function Banner({ kind, children }: { kind: 'err' | 'warn' | 'info'; children: ReactNode }) {
  return <div className={`banner ${kind}`} role={kind === 'err' ? 'alert' : 'status'}>{children}</div>;
}

export function Spinner({ label = 'laden…' }: { label?: string }) {
  return <p className="muted" role="status">{label}</p>;
}
